import { submitServerChange } from 'components/dataCreationForms/common';
import { OpenCreateDialogParams } from 'components/dataCreationForms/types';
import { areObjectIdsEqual } from 'helpers/idComparison';
import { useSites } from 'store/site';
import { getCompaniesStoreState } from 'store/company';
import {
  companyOnViewToCompanyCreateModel,
  createEmptyCompany,
} from 'shared/domain/company/mapping';
import { companyClient } from 'shared/domain/company/companyClient';
import { CompanyOnView } from 'shared/domain/company/types/view';
import { toOptionalUserWithPersonalData } from 'shared/domain/user/mapping/toView';
import { keepUndeleted } from 'shared/domain/deletable/filters';
import { toLabelledEntities } from 'helpers/misc';
import { useCancelConfirmation } from 'presentation/dialogForms/dialogFormsHooks';
import {
  ComponentType,
  ReactElement,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { useIntl } from 'react-intl';
import {
  useCurrentUserInProjectLabelled,
  useCurrentUserInProjectModel,
} from 'store/user';
import { useNavigate } from 'react-router-dom';
import { useProject } from 'store/project';
import { Identificable } from 'shared/types/commonView';
import { nowTimezonedDateTime } from 'shared/utils/date/dates';
import { CreateCompanyContext } from '../types';
import { handleFormValidation, validate } from '../validation';
import { useAppNavigation } from 'contexts/navigation';
const formName = 'company';
const SUBMIT_EVENT_TYPE = 'submit-company';
const WithCreateCompanyContext = createContext<
  CreateCompanyContext | undefined
>(undefined);

function WithCreateCompany({
  children,
  onSuccessCallback,
}: {
  children: ReactElement;
  onSuccessCallback?: (_id: string, label: string) => void;
}): ReactElement {
  const { goToCompanyDetails } = useAppNavigation();
  const intl = useIntl();
  const {
    items: sites,
    loading: loadingSites,
  } = useSites();
  const [isPosting, setIsPosting] = useState<boolean>(false);
  const availableSites = useMemo(
    () => toLabelledEntities(sites.filter(keepUndeleted)),
    [sites]
  );
  const releaseSubmitEvent = useCallback(() => {
    window.dispatchEvent(new CustomEvent(SUBMIT_EVENT_TYPE));
  }, []);

  const currentUserLabelled = useCurrentUserInProjectLabelled();
  const currentUserModel = useCurrentUserInProjectModel();

  const { timezone, _id: projectId } = useProject();

  const emptyCompany = createEmptyCompany(
    intl,
    nowTimezonedDateTime(timezone),
    currentUserLabelled
  );
  const [initialValues, setInitialValues] =
    useState<CompanyOnView>(emptyCompany);

  const [open, setOpen] = useState(false);
  const openDialog = useCallback(
    ({ freeSoloLabel, siteIds }: OpenCreateDialogParams) => {
      setOpen(true);

      let currentValues = emptyCompany;

      if (freeSoloLabel) {
        currentValues = { ...currentValues, shortLabel: freeSoloLabel };
      }

      if (siteIds?.length) {
        const matchedSites = availableSites.filter((site) =>
          siteIds.some((siteId) => areObjectIdsEqual(site._id, siteId))
        );
        if (matchedSites.length) {
          currentValues = { ...currentValues, sites: matchedSites };
        }
      }

      setInitialValues(currentValues);
    },
    [emptyCompany, availableSites]
  );
  const onSuccess = useCallback(
    (_id, label) => onSuccessCallback?.(_id, label),
    [onSuccessCallback]
  );

  const confirmCancel = useCancelConfirmation(formName);
  const openConfirmationDialog = useCallback(async () => {
    const { cancelConfirmed } = await confirmCancel();
    if (!cancelConfirmed) {
      return;
    }
    setOpen(false);
  }, [confirmCancel]);

  const submitForm = useCallback(
    async (values: CompanyOnView) => {
      const companyCreateModel = companyOnViewToCompanyCreateModel(
        values,
        projectId,
        toOptionalUserWithPersonalData(currentUserModel)
      );
      const outcome = await submitServerChange({
        change: () => companyClient.create(companyCreateModel),
        setOpen,
        setIsPosting,
        action: 'creating a company',
      });
      if (!outcome.saved) {
        return;
      }

      const company = outcome.result;
      // The details view reads the company from the store.
      await getCompaniesStoreState().actions.reload();
      onSuccess(company._id, company.shortLabel || values.shortLabel);
      if (window.location.pathname.includes('/company/create')) {
        goToCompanyDetails(company._id);
      }
    },
    [currentUserModel, goToCompanyDetails, onSuccess, projectId]
  );

  const ctx: CreateCompanyContext = useMemo(() => {
    return {
      open,
      openDialog,
      closeDialog: openConfirmationDialog,
      submitForm,
      isPosting,
      initialValues,
      SUBMIT_EVENT_TYPE,
      releaseSubmitEvent,
      validate,
      handleFormValidation,
      availableSites,
      onSuccess,
      formName,
    };
  }, [
    openConfirmationDialog,
    initialValues,
    isPosting,
    open,
    openDialog,
    releaseSubmitEvent,
    submitForm,
    availableSites,
    onSuccess,
  ]);

  return (
    <WithCreateCompanyContext.Provider value={ctx}>
      {children}
    </WithCreateCompanyContext.Provider>
  );
}

function useCreateCompany(): CreateCompanyContext {
  const context = useContext(WithCreateCompanyContext);
  if (context === undefined) {
    throw new Error(
      'useCreateCompany must be used within an CreateCompanyContext'
    );
  }
  return context;
}

const withCreateCompany =
  (Component: ComponentType<any>) =>
  ({ ...props }): ReactElement => (
    <WithCreateCompany>
      <Component {...props} />
    </WithCreateCompany>
  );

export { WithCreateCompany, useCreateCompany, withCreateCompany };
